import clsx from "clsx";
import type { ReactNode } from "react";

import { ChatSequence } from "./ChatSequence";

/** Three "window control" dots used in the device/chat headers. */
export function WindowDots() {
  return (
    <div aria-hidden className="flex items-center gap-1">
      <span className="h-1.5 w-1.5 rounded-full bg-black/15" />
      <span className="h-1.5 w-1.5 rounded-full bg-black/15" />
      <span className="h-1.5 w-1.5 rounded-full bg-black/15" />
    </div>
  );
}

export function MockupFrame({
  children,
  className,
  rounded = "top",
}: Readonly<{
  children: ReactNode;
  className?: string;
  /** "top" for bleed layouts; "all" when the frame is inset inside a parent card. */
  rounded?: "top" | "all";
}>) {
  return (
    <div
      aria-hidden
      className={clsx(
        "flex h-full flex-col overflow-hidden border border-black/5 bg-marketing-industry-card",
        rounded === "all" ? "rounded-2xl" : "rounded-t-2xl border-b-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Static decorative voice waveform. */
function MiniWaveform() {
  const bars = [
    6, 10, 16, 9, 22, 14, 28, 18, 11, 24, 16, 30, 20, 12, 26, 14, 9, 18, 24, 13,
    8, 20, 28, 15, 10, 22, 17, 26, 12, 19, 9, 14,
  ];
  return (
    <div className="flex h-9 items-center gap-[3px]">
      {bars.map((height, index) => (
        <span
          key={`${height}-${index}`}
          className="animate-voice-wave w-[3px] rounded-full bg-black/20"
          style={{
            height: `${height}px`,
            animationDelay: `${(index % 8) * 0.09}s`,
            animationDuration: `${0.9 + (index % 3) * 0.25}s`,
          }}
        />
      ))}
    </div>
  );
}

export type VoiceChannelMockupProps = {
  header: string;
  agentName: string;
  agentMeta: string;
  liveLabel: string;
  customerLabel: string;
  customerMessage: string;
};

export function VoiceChannelMockup({
  header,
  agentName,
  agentMeta,
  liveLabel,
  customerLabel,
  customerMessage,
}: Readonly<VoiceChannelMockupProps>) {
  return (
    <MockupFrame>
      <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-3 py-2">
        <WindowDots />
        <span className="font-monument text-[0.625rem] text-black/70">
          {header}
        </span>
      </div>
      <div className="flex-1 space-y-3 p-3">
        <div className="rounded-xl border border-black/5 bg-black/[0.02] p-2.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gail-primary font-monument text-xs font-medium text-white">
              {agentName.charAt(0)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-monument text-xs font-medium text-black">
                {agentName}
              </p>
              <p className="truncate font-monument text-[0.625rem] text-black/70">
                {agentMeta}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 font-monument text-[0.625rem] font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {liveLabel}
            </span>
          </div>
          <div className="mt-2.5 flex justify-center">
            <MiniWaveform />
          </div>
        </div>
        <div className="rounded-xl border border-black/5 bg-white p-2.5">
          <p className="font-mono text-[0.5rem] uppercase tracking-[0.14em] text-black/70">
            {customerLabel}
          </p>
          <p className="mt-1 font-monument text-xs leading-snug text-black/80">
            {customerMessage}
          </p>
        </div>
      </div>
    </MockupFrame>
  );
}

export type SmsChannelMockupProps = {
  header: string;
  day: string;
  customerMessage: string;
  reply: string;
  customerReply: string;
  sequenceGroup?: string;
  sequenceIndex?: number;
};

export function SmsChannelMockup({
  header,
  day,
  customerMessage,
  reply,
  customerReply,
  sequenceGroup,
  sequenceIndex,
}: Readonly<SmsChannelMockupProps>) {
  return (
    <MockupFrame>
      <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-3 py-2">
        <WindowDots />
        <span className="truncate font-monument text-[0.625rem] text-black/70">
          {header}
        </span>
      </div>
      <div className="flex-1 space-y-2 p-3">
        <p className="text-center font-monument text-[0.5625rem] uppercase tracking-[0.12em] text-black/70">
          {day}
        </p>
        <ChatSequence
          className="space-y-2"
          sequenceGroup={sequenceGroup}
          sequenceIndex={sequenceIndex}
          steps={[
            {
              align: "end",
              bubble: (
                <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 font-monument text-xs leading-snug text-white">
                  {customerMessage}
                </p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName:
                "rounded-2xl rounded-bl-sm bg-black/[0.05] text-black/70",
              bubble: (
                <p className="max-w-[80%] rounded-2xl rounded-bl-sm bg-black/[0.05] px-3 py-2 font-monument text-xs leading-snug text-black/80">
                  {reply}
                </p>
              ),
            },
            {
              align: "end",
              bubble: (
                <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 font-monument text-xs leading-snug text-white">
                  {customerReply}
                </p>
              ),
            },
          ]}
        />
      </div>
    </MockupFrame>
  );
}

export type ChatChannelMockupProps = {
  company: string;
  status: string;
  greeting: string;
  option1: string;
  option2: string;
  customerMessage: string;
  sequenceGroup?: string;
  sequenceIndex?: number;
};

export function ChatChannelMockup({
  company,
  status,
  greeting,
  option1,
  option2,
  customerMessage,
  sequenceGroup,
  sequenceIndex,
}: Readonly<ChatChannelMockupProps>) {
  return (
    <MockupFrame>
      <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-3 py-2.5">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <span className="font-monument text-[0.6875rem] font-medium text-black">
          {company}
        </span>
        <span className="ml-auto font-monument text-[0.625rem] text-black/70">
          {status}
        </span>
      </div>
      <div className="flex-1 p-3">
        <ChatSequence
          className="space-y-2"
          sequenceGroup={sequenceGroup}
          sequenceIndex={sequenceIndex}
          steps={[
            {
              align: "start",
              typing: true,
              typingClassName:
                "rounded-2xl rounded-bl-sm bg-black/[0.05] text-black/70",
              bubble: (
                <p className="max-w-[85%] rounded-2xl rounded-bl-sm bg-black/[0.05] px-3 py-2 font-monument text-xs leading-snug text-black/80">
                  {greeting}
                </p>
              ),
            },
            {
              align: "start",
              bubble: (
                <span className="flex flex-wrap gap-1.5">
                  <span className="rounded-full border border-gail-primary/30 px-2.5 py-1 font-monument text-[0.625rem] font-medium text-gail-primary">
                    {option1}
                  </span>
                  <span className="rounded-full border border-gail-primary/30 px-2.5 py-1 font-monument text-[0.625rem] font-medium text-gail-primary">
                    {option2}
                  </span>
                </span>
              ),
            },
            {
              align: "end",
              bubble: (
                <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-neutral-900 px-3 py-2 font-monument text-xs leading-snug text-white">
                  {customerMessage}
                </p>
              ),
            },
          ]}
        />
      </div>
    </MockupFrame>
  );
}

export type WhatsAppChannelMockupProps = {
  company: string;
  status: string;
  customerMessage: string;
  reply1: string;
  customerReply: string;
  reply2: string;
  sequenceGroup?: string;
  sequenceIndex?: number;
};

export function WhatsAppChannelMockup({
  company,
  status,
  customerMessage,
  reply1,
  customerReply,
  reply2,
  sequenceGroup,
  sequenceIndex,
}: Readonly<WhatsAppChannelMockupProps>) {
  return (
    <MockupFrame>
      <div className="flex shrink-0 items-center gap-2 bg-[#075e54] px-3 py-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 font-monument text-[0.625rem] font-medium text-white">
          {company.charAt(0)}
        </span>
        <div className="min-w-0">
          <p className="truncate font-monument text-[0.6875rem] font-medium text-white">
            {company}
          </p>
          <p className="font-monument text-[0.5625rem] text-white/70">
            {status}
          </p>
        </div>
      </div>
      <div className="flex-1 bg-[#ece5dd] p-3">
        <ChatSequence
          className="space-y-2"
          sequenceGroup={sequenceGroup}
          sequenceIndex={sequenceIndex}
          steps={[
            {
              align: "end",
              bubble: (
                <p className="inline-flex max-w-[85%] items-end gap-1 rounded-lg rounded-tr-sm bg-[#dcf8c6] px-2.5 py-1.5 font-monument text-xs leading-snug text-black/80 shadow-sm">
                  {customerMessage}
                  <span className="text-[0.625rem] text-sky-500">✓✓</span>
                </p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName:
                "rounded-lg rounded-tl-sm bg-white text-black/70 shadow-sm",
              bubble: (
                <p className="max-w-[85%] rounded-lg rounded-tl-sm bg-white px-2.5 py-1.5 font-monument text-xs leading-snug text-black/80 shadow-sm">
                  {reply1}
                </p>
              ),
            },
            {
              align: "end",
              bubble: (
                <p className="inline-flex max-w-[85%] items-end gap-1 rounded-lg rounded-tr-sm bg-[#dcf8c6] px-2.5 py-1.5 font-monument text-xs leading-snug text-black/80 shadow-sm">
                  {customerReply}
                  <span className="text-[0.625rem] text-sky-500">✓✓</span>
                </p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName:
                "rounded-lg rounded-tl-sm bg-white text-black/70 shadow-sm",
              bubble: (
                <p className="max-w-[85%] rounded-lg rounded-tl-sm bg-white px-2.5 py-1.5 font-monument text-xs leading-snug text-black/80 shadow-sm">
                  {reply2}
                </p>
              ),
            },
          ]}
        />
      </div>
    </MockupFrame>
  );
}
